// Pieces shared by every request screen: steppers, the event timeline, property/person cards, slot lines.
import type { ReactNode } from 'react';
import { PROPERTY_TYPE_LABELS, formatSar, type BrokerRequestStatus, type EvaluationStatus, type Role } from '@wasity/shared';
import { Avatar, Bar, Button, Card, KV, Notice, Stepper, Timeline } from '@/components/ui';
import { useI18n, formatDateTime } from '@/i18n';
import { ApiError } from '@/lib/api';
import { errorText } from '@/lib/errors';
import { money, phoneDisplay } from '@/lib/format';
import type { Person, Property, RequestEvent, Slots } from '@/lib/types';

/**
 * Why accepting or declining an assignment failed, in the staff member's terms: the window ran out, the
 * request is no longer theirs (declined, reassigned, expired, or already accepted), or something else.
 */
export function assignmentFailure(e: unknown): 'expired' | 'notYours' | string {
  if (!(e instanceof ApiError)) return 'error';
  if (e.code === 'acceptance_window_expired') return 'expired';
  // The request moved on: reassigned, already accepted, returned to the queue, or gone. `forbidden`
  // belongs here too. The broker and valuer mounts already require the role (`routes/broker.ts:29`,
  // `routes/evaluator.ts:14`) and `assertOwns` exempts admins (`domain/common.ts`), so the only 403
  // reachable from an accept or decline is the ownership check — which is exactly "not yours any more".
  if (e.code === 'forbidden' || e.code === 'invalid_state' || e.code === 'already_assigned' || e.status === 404) return 'notYours';
  return e.code;
}
export function AssignmentErrorNotice({ error }: { error: string | null }) {
  const { t } = useI18n();
  if (!error) return null;
  return <Notice tone="error">{error === 'expired' ? t('broker.incoming.expiredNotice') : error === 'notYours' ? t('broker.incoming.noLongerYours') : errorText(error, t)}</Notice>;
}

export const BR_STEP_INDEX: Record<BrokerRequestStatus, number> = { new: 0, assigned: 1, active: 2, contract_signed: 3, licensed: 4, offer_pending: 5, completed: 7, cancelled: -1 };
export const EV_STEP_INDEX: Record<EvaluationStatus, number> = { new: 0, assigned: 1, accepted: 2, fee_set: 3, in_progress: 5, report_ready: 6, completed: 7, cancelled: -1 };

export function BrStepper({ status, mine }: { status: BrokerRequestStatus; mine?: boolean }) {
  const { t } = useI18n();
  const steps = [
    { key: 'sent', label: t('steps.br.sent') },
    { key: 'assigned', label: t(mine ? 'steps.brMine.assigned' : 'steps.br.assigned') },
    { key: 'accepted', label: t(mine ? 'steps.brMine.accepted' : 'steps.br.accepted') },
    { key: 'contract', label: t('steps.br.contract') },
    { key: 'licensed', label: t('steps.br.licensed') },
    { key: 'offer', label: t('steps.br.offer') },
    { key: 'completed', label: t('steps.br.completed') },
  ];
  return <Stepper steps={steps} current={BR_STEP_INDEX[status]} cancelled={status === 'cancelled'} />;
}
export function EvStepper({ status, mine }: { status: EvaluationStatus; mine?: boolean }) {
  const { t } = useI18n();
  const steps = [
    { key: 'sent', label: t('steps.ev.sent') },
    { key: 'assigned', label: t(mine ? 'steps.evMine.assigned' : 'steps.ev.assigned') },
    { key: 'accepted', label: t(mine ? 'steps.evMine.accepted' : 'steps.ev.accepted') },
    { key: 'fee', label: t(mine ? 'steps.evMine.fee' : 'steps.ev.fee') },
    { key: 'paid', label: t(mine ? 'steps.evMine.paid' : 'steps.ev.paid') },
    { key: 'inProgress', label: t('steps.ev.inProgress') },
    { key: 'report', label: t(mine ? 'steps.evMine.report' : 'steps.ev.report') },
  ];
  return <Stepper steps={steps} current={EV_STEP_INDEX[status]} cancelled={status === 'cancelled'} />;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
const num = (v: unknown) => (typeof v === 'number' ? v : null);

/**
 * The request's history, in the viewer's words: a broker reads "you accepted", an admin reads
 * "the broker accepted". `names` maps user ids to display names for the assignment lines.
 */
export function RequestTimeline({ events, viewer, names = {} }: { events: RequestEvent[]; viewer: Role; names?: Record<string, string | null | undefined> }) {
  const { t, locale } = useI18n();
  const who = (e: RequestEvent) => {
    if (!e.actorRole) return null;
    if (e.actorRole === 'user') return t('app.owner');
    if (e.actorRole === 'admin') return t('app.adminRole');
    if (e.actorRole === viewer && e.actorUserId) return t('app.you');
    return t(e.actorRole === 'broker' ? 'app.broker' : 'app.evaluator');
  };
  const items = events.map((e) => {
    const p = e.payload ?? {};
    let title: ReactNode = t(`events.${e.type}`);
    let body: ReactNode = null;
    let tone: 'warning' | 'muted' | undefined;
    switch (e.type) {
      case 'received':
        tone = 'muted';
        break;
      case 'assigned': {
        const id = str(p.brokerUserId) ?? str(p.evaluatorUserId);
        title = id && viewer !== 'admin' && (viewer === 'broker' || viewer === 'evaluator') ? t('events.assignedYou') : t('events.assigned', { name: (id && names[id]) || '' });
        if (str(p.deadline)) body = t('events.acceptBy', { time: formatDateTime(str(p.deadline)!, locale) });
        break;
      }
      case 'accepted':
        title = viewer === 'admin' || viewer === 'user' ? t(e.kind === 'EV' ? 'events.acceptedEv' : 'events.accepted') : t('events.acceptedYou');
        break;
      case 'declined':
        // The same event is written when a valuer gives back a request they had already accepted (`afterAccepting`).
        title = t(p.afterAccepting ? 'events.withdrewAfterAccepting' : 'events.declined', { who: t(e.kind === 'EV' ? 'app.evaluator' : 'app.broker') });
        body = str(p.reason);
        tone = 'warning';
        break;
      case 'expired':
        tone = 'warning';
        break;
      case 'licensed':
        body = str(p.licenceNo) ? t('events.licenceNo', { no: str(p.licenceNo)! }) : null;
        break;
      case 'contract_signed':
        body = t('events.contractBody');
        break;
      case 'listed':
        title = t('events.listed', { platform: str(p.platform) ?? '' });
        body = str(p.url) ? (
          <a href={str(p.url)!} target="_blank" rel="noreferrer" className="ltr num">
            {str(p.url)}
          </a>
        ) : null;
        break;
      case 'note':
        title = viewer === 'broker' ? t('events.noteYou') : t('events.note');
        body = str(p.text) ? `«${str(p.text)}»` : null;
        break;
      case 'offer':
        title = t('events.offer', { amount: money(num(p.amountHalalas), locale) });
        body = str(p.terms) ? `«${str(p.terms)}»` : null;
        tone = 'warning';
        break;
      case 'offer_accepted':
        title = t('events.offer_accepted', { amount: money(num(p.amountHalalas), locale) });
        break;
      case 'offer_rejected':
        body = str(p.reason);
        break;
      case 'offer_withdrawn':
        body = str(p.reason) === 'withdrawn_by_broker' ? null : str(p.reason);
        tone = 'muted';
        break;
      case 'fee_set':
        body = t('events.feeBody', { fee: money(num(p.fee), locale), service: money(num(p.service), locale), total: money(num(p.total), locale) });
        tone = 'warning';
        break;
      case 'paid':
        if (p.afterCancel) {
          title = t('events.paidAfterCancel');
          tone = 'warning';
        }
        body = num(p.amountHalalas) != null ? money(num(p.amountHalalas), locale) : null;
        break;
      case 'payment_failed':
        body = str(p.problem) ?? str(p.status);
        tone = 'warning';
        break;
      case 'commission_due':
        title = t('events.commission_due', { amount: money(num(p.commissionHalalas), locale) });
        tone = 'warning';
        break;
      case 'cancelled':
        body = str(p.reason);
        tone = 'muted';
        break;
      default:
        break;
    }
    return { id: e.id, title, body, at: e.createdAt, who: who(e), tone };
  });
  return <Timeline items={items} />;
}

const FEATURE_LABELS: Record<string, [string, string]> = {
  kitchen: ['مطبخ', 'Kitchen'], elevator: ['مصعد', 'Elevator'], driverRoom: ['غرفة سائق', 'Driver room'], maidRoom: ['غرفة خادمة', 'Maid room'], garage: ['كراج', 'Garage'], pool: ['مسبح', 'Pool'],
  basement: ['قبو', 'Basement'], furnished: ['مفروش', 'Furnished'], water: ['ماء', 'Water'], electricity: ['كهرباء', 'Electricity'], drainage: ['صرف صحي', 'Drainage'], ac: ['تكييف', 'A/C'], garden: ['حديقة', 'Garden'],
  // Offered by the add-property form (`FEATURES` there); without an entry the card printed the key.
  duplex: ['دوبلكس', 'Duplex'], waterTank: ['خزان ماء', 'Water tank'],
};
const FACADE: Record<string, [string, string]> = { north: ['شمالية', 'North'], south: ['جنوبية', 'South'], east: ['شرقية', 'East'], west: ['غربية', 'West'], corner: ['زاوية', 'Corner'] };

export function PropertyCard({ property, photos, title }: { property: Property; photos?: { id: string; url: string | null }[]; title?: string }) {
  const { t, locale } = useI18n();
  const ar = locale === 'ar';
  const purpose = ar ? (property.purpose === 'sell' ? 'للبيع' : 'للإيجار') : property.purpose === 'sell' ? 'for sale' : 'for rent';
  const rooms = [property.bedrooms != null && `${property.bedrooms} ${ar ? 'نوم' : 'bed'}`, property.bathrooms != null && `${property.bathrooms} ${ar ? 'دورات مياه' : 'bath'}`, property.livingRooms != null && `${property.livingRooms} ${ar ? 'صالة' : 'living'}`].filter(Boolean).join(' · ');
  const features = Object.entries(property.features ?? {})
    .filter(([, v]) => v === true)
    .map(([k]) => FEATURE_LABELS[k]?.[ar ? 0 : 1] ?? k)
    .join(' · ');
  const facade = [property.facade && (FACADE[property.facade]?.[ar ? 0 : 1] ?? property.facade), property.streetWidthM != null && `${ar ? 'شارع' : 'street'} ${property.streetWidthM} ${ar ? 'م' : 'm'}`].filter(Boolean).join(' · ');
  const items = [
    { k: t('app.kind'), v: `${PROPERTY_TYPE_LABELS[property.type][locale]} · ${purpose}` },
    { k: ar ? 'المساحة' : 'Area', v: `${property.areaM2} ${ar ? 'م²' : 'm²'}`, num: true },
    ...(rooms ? [{ k: ar ? 'الغرف' : 'Rooms', v: rooms, num: true }] : []),
    ...(property.floors != null ? [{ k: ar ? 'الأدوار' : 'Floors', v: String(property.floors), num: true }] : []),
    ...(property.ageYears != null ? [{ k: ar ? 'العمر' : 'Age', v: `${property.ageYears} ${ar ? 'سنوات' : 'years'}`, num: true }] : []),
    ...(facade ? [{ k: ar ? 'الواجهة' : 'Facade', v: facade }] : []),
    ...(features ? [{ k: ar ? 'المزايا' : 'Features', v: features }] : []),
    ...(property.ownerEstimateHalalas != null ? [{ k: t('dashboard.ownerEstimate'), v: formatSar(property.ownerEstimateHalalas, locale), num: true }] : []),
    {
      k: ar ? 'الموقع' : 'Location',
      v: (
        <span>
          {[property.street, property.district, property.city].filter(Boolean).join(ar ? '، ' : ', ')} ·{' '}
          <a href={`https://maps.google.com/?q=${property.lat},${property.lng}`} target="_blank" rel="noreferrer">
            {t('app.map')}
          </a>
        </span>
      ),
    },
    ...(property.notes ? [{ k: t('app.notes'), v: <span className="text-fg-2">«{property.notes}»</span> }] : []),
  ];
  return (
    <Card>
      <h2 className="mb-4">{title ?? t('app.property')}</h2>
      <KV items={items} />
      {photos && photos.length ? (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {photos.slice(0, 4).map((ph) => (
            <PhotoThumb key={ph.id} url={ph.url} />
          ))}
          {photos.length > 4 ? <span className="text-[13px] text-muted self-center">+{photos.length - 4}</span> : null}
        </div>
      ) : null}
    </Card>
  );
}

export function PhotoThumb({ url, size = 72 }: { url: string | null; size?: number }) {
  const usable = url && !url.startsWith('mock://');
  return usable ? (
    <img src={url} alt="" width={size} height={Math.round(size * 0.75)} className="rounded-sm object-cover border border-line" style={{ width: size, height: Math.round(size * 0.75) }} loading="lazy" />
  ) : (
    <span className="skeleton inline-block" style={{ width: size, height: Math.round(size * 0.75) }} aria-hidden />
  );
}

export function PersonCard({ title, person, subtitle, action, contact, children }: { title: string; person: Person | null | undefined; subtitle?: ReactNode; action?: ReactNode; contact?: boolean; children?: ReactNode }) {
  const { t } = useI18n();
  return (
    <Card>
      <h2 className="mb-3">{title}</h2>
      {person ? (
        <>
          <div className="flex items-center gap-3">
            <Avatar name={person.name} />
            <div className="min-w-0">
              <p className="font-medium truncate">{person.name}</p>
              {subtitle ? <p className="text-[13px] text-muted">{subtitle}</p> : null}
              {person.phone ? <p className="text-[13px] text-muted ltr num">{phoneDisplay(person.phone)}</p> : null}
            </div>
            {action ? <span className="ms-auto">{action}</span> : null}
          </div>
          {contact && person.phone ? (
            <div className="flex gap-2 mt-3">
              <Button variant="secondary" className="flex-1 min-h-9 text-[14px]" onClick={() => (window.location.href = `tel:${person.phone}`)}>
                {t('app.call')}
              </Button>
              <Button variant="secondary" className="flex-1 min-h-9 text-[14px]" onClick={() => window.open(`https://wa.me/${person.phone!.replace('+', '')}`, '_blank', 'noreferrer')}>
                {t('app.whatsapp')}
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <p className="text-muted text-[14px]">—</p>
      )}
      {children}
    </Card>
  );
}

export function SlotLine({ slots, className }: { slots: Pick<Slots, 'used' | 'max'>; className?: string }) {
  const { t } = useI18n();
  return (
    <div className={className}>
      <div className="flex justify-between text-[13px]">
        <span className="text-muted">{t('app.slots')}</span>
        <span className="num">
          {slots.used} / {slots.max}
        </span>
      </div>
      <Bar value={slots.used} max={slots.max} className="mt-1" />
    </div>
  );
}
